import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFeatures } from '../utils/features';
import FindProgress from './FindProgress';
import {
  assignBeaconAsking, releaseBeacon, batteryLabel, chargeBadge, lastSeenLabel, beaconOptionLabel,
  screenStatus, screenQr, currentActor,
} from '../utils/beacons';
import { lotBeaconWhere, useLotBeacon } from '../features/materials/lotBeacons';

// A QR-shaped mark standing in for the code the beacon draws (no QR library in the bundle):
// the three finder squares and a few modules. The link it holds is its title.
const QR_MODULES = [[9, 1], [11, 3], [9, 5], [12, 7], [1, 9], [4, 10], [7, 12], [10, 9], [13, 11], [15, 9],
  [17, 12], [11, 14], [14, 15], [16, 17], [19, 15], [9, 18], [12, 19], [18, 19], [20, 21], [15, 20], [9, 21]];

function BeaconQrMark({ link }) {
  const finder = (x, y) => (
    <g key={`${x}-${y}`}>
      <rect x={x} y={y} width="7" height="7" fill="#111" />
      <rect x={x + 1} y={y + 1} width="5" height="5" fill="#eceae4" />
      <rect x={x + 2} y={y + 2} width="3" height="3" fill="#111" />
    </g>
  );
  return (
    <svg
      data-testid="beacon-screen-qr"
      role="img"
      aria-label={`QR code: ${link}`}
      viewBox="-1 -1 25 25"
      width="58"
      height="58"
      style={{ flex: '0 0 auto', alignSelf: 'center' }}
    >
      <title>{link}</title>
      {finder(0, 0)}{finder(16, 0)}{finder(0, 16)}
      {QR_MODULES.map(([x, y]) => <rect key={`${x}.${y}`} x={x} y={y} width="1.6" height="1.6" fill="#111" />)}
    </svg>
  );
}

const NBSP = '\u00a0';

/**
 * The pallet's e-paper as it should read (jobq-api pallet_beacons.screen_lines, firmware
 * "Option B"): six lines, line 1 big (TRK / beacon ID), line 4 medium (sheets left), the rest
 * small, and the QR on the right when the screen has one (`qr`: the link it holds). A pallet on
 * firmware 0.4+ gets SEVEN lines ("Owner: …" as line 2), and the beacon then draws line 5 medium:
 * it picks its layout by how many lines came, so the preview does too.
 */
export function BeaconScreenPreview({ lines, qr = null }) {
  const all = Array.isArray(lines) ? lines : [];
  const medium = all.length >= 7 ? 4 : 3;
  const size = (i) => (i === 0 ? '1.15rem' : i === medium ? '0.92rem' : '0.72rem');
  return (
    <div
      data-testid="beacon-screen"
      style={{
        background: '#eceae4', color: '#111', border: '2px solid #333', borderRadius: 4,
        padding: '6px 8px 6px 10px', fontFamily: 'Menlo, Consolas, monospace', minWidth: 230, lineHeight: 1.25,
        display: 'flex', gap: 8, alignItems: 'stretch',
      }}
    >
      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
        {all.map((l, i) => (
          <div
            key={i}
            data-testid={`beacon-screen-line-${i + 1}`}
            style={{ fontSize: size(i), fontWeight: i === 0 || i === medium ? 700 : 400, whiteSpace: 'nowrap' }}
          >
            {l || NBSP}
          </div>
        ))}
      </div>
      {qr && <BeaconQrMark link={qr} />}
    </div>
  );
}

/**
 * Beacon card for one lot (TRK): which beacon is on it, its battery / last contact / dock, what
 * its screen says, and Find / Release; or a dropdown of free beacons to assign. Renders nothing
 * unless GET /features says pallet_beacons is on. A failed call shows a message and never
 * blocks the popup around it.
 */
export default function BeaconCard({ mtnId }) {
  const features = useFeatures();
  const enabled = features.palletBeacons.enabled;
  const [choice, setChoice] = useState('');
  // The lot's beacon, its Find and the watch while it lands: features/materials/lotBeacons (shared
  // with the phone lot panel). Assign / Release ride on its `run`.
  const {
    data, beacon, loadError, busy, msg, setMsg, load, run, find: handleFind, finder, finding, activeFind,
  } = useLotBeacon(mtnId, enabled);

  useEffect(() => { setChoice(''); }, [mtnId, enabled]);

  if (!enabled || !mtnId) return null;

  const handleAssign = async () => {
    if (!choice) return;
    // a beacon on another live lot asks before it is moved (utils/beacons assignBeaconAsking)
    const r = await run(
      () => assignBeaconAsking(mtnId, choice, { actor: currentActor() }),
      (b, res) => (res.moved ? `${b.beacon_id} moved to this pallet.` : `${b.beacon_id} is on this pallet.`),
    );
    if (r.ok) setChoice('');
    else if (!r.cancelled) setMsg({ tone: 'danger', text: r.message });
  };

  const handleRelease = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Take ${beacon.beacon_id} off this pallet? Its screen will say Available.`)) return;
    const r = await run(() => releaseBeacon(mtnId, currentActor()), () => `${beacon.beacon_id} is free again.`);
    if (!r.ok) setMsg({ tone: 'danger', text: r.message });
  };

  const scr = screenStatus(beacon);
  // where the pallet was last seen on the shop map ("Metal room · near Rack A · last seen 10:02 AM (2 h ago)");
  // the dock badge already says "Plugged in at Laser"
  const whereLine = lotBeaconWhere(beacon, features.palletBeacons.location);

  return (
    <div className="card mb-3 border-primary-subtle" data-testid="beacon-card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <strong><i className="bi bi-broadcast me-1"></i>Beacon</strong>
        {beacon && (
          <span className="small text-muted">
            {beacon.assigned_by ? `Assigned by ${beacon.assigned_by}` : 'Assigned'}
            {beacon.assigned_at ? ` · ${new Date(beacon.assigned_at).toLocaleString()}` : ''}
          </span>
        )}
      </div>
      <div className="card-body">
        {!data && !loadError && <div className="small text-muted">Loading beacon…</div>}
        {loadError && !data && <div className="small text-danger">Beacon info unavailable: {loadError}</div>}

        {beacon && (
          <div className="d-flex flex-wrap gap-3 align-items-start">
            <div>
              <div className="fs-4 fw-bold font-monospace" data-testid="beacon-id">{beacon.beacon_id}</div>
              <div className="small">
                <i className="bi bi-battery-half me-1"></i>{batteryLabel(beacon)}
                {beacon.charging ? <span className="badge bg-success ms-1">charging</span> : null}
                {chargeBadge(beacon) && (
                  <span className={`badge ms-1 ${chargeBadge(beacon).className}`} data-testid="charge-badge">
                    {chargeBadge(beacon).text}
                  </span>
                )}
              </div>
              <div className="small">
                {beacon.in_range
                  ? <span className="text-success"><i className="bi bi-circle-fill me-1" style={{ fontSize: '0.5rem' }}></i>In range</span>
                  : !whereLine && <span className="text-muted">Last heard {lastSeenLabel(beacon.last_seen_at)}</span>}
              </div>
              {whereLine && (
                <div className="small text-muted" data-testid="beacon-where" style={{ maxWidth: 320 }}>
                  <i className="bi bi-geo-alt me-1"></i>{whereLine}
                </div>
              )}
              {beacon.docked_station && (
                <div className="small"><span className="badge bg-info text-dark">Plugged in at {beacon.docked_station}</span></div>
              )}
            </div>
            <div>
              <BeaconScreenPreview lines={beacon.screen.lines} qr={screenQr(beacon)} />
              <div className={`small mt-1 text-${scr.tone}`} data-testid="beacon-screen-status">{scr.text}</div>
            </div>
            <div className="d-flex flex-column gap-2 ms-auto">
              <button type="button" className="btn btn-sm btn-success" onClick={handleFind} disabled={busy || finding}>
                <i className="bi bi-lightbulb me-1"></i>{finding ? 'Finding…' : 'Find (flash)'}
              </button>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleRelease} disabled={busy}>
                Release
              </button>
            </div>
            {activeFind && (
              // a full-width row: with the shop map on, the Find carries a map of where the pallet is
              <div className="w-100" data-testid="beacon-find-row">
                <FindProgress find={activeFind} onRetry={handleFind} onChange={finder.track(beacon.beacon_id)}
                  onSettled={load} />
              </div>
            )}
            {beacon.lot_done && (
              <div className="w-100 small text-muted">
                This lot is done: the beacon frees itself the next time a gateway hears it.
              </div>
            )}
          </div>
        )}

        {data && !beacon && data.lot_done && (
          <div className="small text-muted">This lot is done: no beacon needed.</div>
        )}

        {data && !beacon && !data.lot_done && (
          data.available.length === 0 ? (
            <div className="small text-muted">
              No free beacons. <Link to="/beacons">Add one on the Beacons page</Link>, or switch one on near a gateway.
            </div>
          ) : (
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <select
                className="form-select form-select-sm"
                style={{ maxWidth: 320 }}
                value={choice}
                onChange={(e) => setChoice(e.target.value)}
                aria-label="Beacon to put on this pallet"
                disabled={busy}
              >
                <option value="">Put a beacon on this pallet…</option>
                {data.available.map((b) => (
                  <option key={b.beacon_id} value={b.beacon_id}>{beaconOptionLabel(b)}</option>
                ))}
              </select>
              <button type="button" className="btn btn-sm btn-primary" onClick={handleAssign} disabled={!choice || busy}>
                Assign
              </button>
            </div>
          )
        )}

        {msg && <div className={`small mt-2 text-${msg.tone}`} role="status">{msg.text}</div>}
      </div>
    </div>
  );
}
